import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import * as MapboxDirections from '@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions';
import { TranslateService } from '@ngx-translate/core';
import * as mapboxgl from 'mapbox-gl';

import { SiteSettingsService } from '@shared/services/site-settings.service';
import { LanguageService } from '@shared/shared/services/language-service';
import { Location, LocationService } from '@shared/shared/services/location-service';
import { MAPBOX_ACCESS_TOKEN } from 'app.config';
import { IDirectionsOptions } from '../models';

@Injectable({
  providedIn: 'root',
})
export class MapDirectionsService {
  public directions: MapboxDirections;

  constructor(
    private locationService: LocationService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private siteSettingsService: SiteSettingsService
  ) {}

  public addDirections(mapboxMap: mapboxgl.Map, options: IDirectionsOptions = {}) {
    const countryCode = this.siteSettingsService.settings.country_code;

    this.directions = new MapboxDirections({
      accessToken: MAPBOX_ACCESS_TOKEN,
      unit: ['GB', 'US', 'LR'].some((x: string) => x === countryCode) ? 'imperial' : 'metric',
      language: this.languageService.currentLanguage,
      styles: this.directionStyle(),
    });

    mapboxMap.addControl(this.directions, 'top-left');

    this.locationService.getLocation().subscribe({
      next: (location: Location) => {
        const mapPopupAnchorBottom = document.getElementsByClassName('mapboxgl-popup-anchor-bottom')[0];
        if (mapPopupAnchorBottom) {
          mapPopupAnchorBottom.remove();
        }

        this.directions.setOrigin([location.long, location.lat]);

        if (options.destination) {
          const bounds: [number, number, number, number] = [location.long, location.lat, ...options.destination];

          this.directions.setDestination(options.destination);
          mapboxMap.fitBounds(bounds, { padding: 64 });
        }
      },
      error: () => {
        this.snackBar.open(this.translateService.instant('ActivateGeolocation:ErrorMessage'), '', {
          duration: 10000,
        });
      },
    });

    // Using route event to check if direction overlay available
    this.directions.on('route', () => {
      /*
       * The MapboxDirections control does not update the coordinates when any map is opened for the second time.
       * It is probably a bug in the library, we update the coordinates here manually to fix it.
       */
      const originInput = document.getElementById('mapbox-directions-origin-input')?.getElementsByTagName('input')[0];
      const destinationInput = document
        .getElementById('mapbox-directions-destination-input')
        ?.getElementsByTagName('input')[0];

      if (originInput) {
        originInput.value =
          this.directions
            .getOrigin()
            ?.geometry.coordinates.map((coord) => coord.toFixed(5))
            .join(',') || '';
      }

      if (destinationInput) {
        destinationInput.value =
          this.directions
            .getDestination()
            ?.geometry.coordinates.map((coord) => coord.toFixed(5))
            .join(',') || '';
      }

      const routeHeaderElement = document.getElementsByClassName('mapbox-directions-route-summary')[0];
      if (routeHeaderElement) {
        routeHeaderElement.addEventListener('click', () => {
          routeHeaderElement.parentElement.classList.toggle('active');
        });
        this.translateDirection();
      }
    });
  }

  private translateDirection() {
    const summaryTime = this.languageService.currentLanguage === 'ja-jp' ? 'h1' : 'span';
    const summaryDistance = this.languageService.currentLanguage === 'ja-jp' ? 'span' : 'h1';

    const directionWrap = document.getElementsByClassName('mapboxgl-ctrl-directions')[0];
    const directionSummary = directionWrap.getElementsByClassName('mapbox-directions-route-summary')[0];
    const directionSummaryTime = directionSummary.getElementsByTagName(summaryTime)[0];
    const directionSummaryDistance = directionSummary.getElementsByTagName(summaryDistance)[0];
    const directionStepDistance = directionWrap.getElementsByClassName('mapbox-directions-step-distance');
    const directionProfileWrap = directionWrap.getElementsByClassName('mapbox-directions-profile')[0];
    const directionProfileLabels = directionProfileWrap.getElementsByTagName('label');
    const directionTranslateTags = [
      'MapDirectionsform:Traffic',
      'MapDirectionsform:Driving',
      'MapDirectionsform:Walking',
      'MapDirectionsform:Cycling',
    ];

    // Translate directions profiles and time units
    // Unfortunately the directions plugin doesn't translate it like the turn-by-turn directions
    // We could avoid this DOM manipulation only by creating our own overlay based on the API.
    Array.from(directionProfileLabels).forEach((element: Element, i: number) => {
      element.innerHTML = this.translateService.instant(directionTranslateTags[i]);
    });

    directionSummaryTime.innerHTML = directionSummaryTime.innerHTML
      .replace('h', ' ' + this.translateService.instant('MapDirectionsTime:Hours'))
      .replace('min', ' ' + this.translateService.instant('MapDirectionsTime:Mins'));
    directionSummaryDistance.innerHTML = directionSummaryDistance.innerHTML
      .replace('mi', ' ' + this.translateService.instant('MapDistance:Miles'))
      .replace('km', ' ' + this.translateService.instant('MapDistance:Km'));

    Array.from(directionStepDistance).forEach((element: Element) => {
      if (!element.innerHTML.match('km') && !element.innerHTML.match('mi')) {
        element.innerHTML = element.innerHTML.replace('m', ' ' + this.translateService.instant('MapDistance:Meter'));
      }

      element.innerHTML = element.innerHTML
        .replace('mi', ' ' + this.translateService.instant('MapDistance:Miles'))
        .replace('km', ' ' + this.translateService.instant('MapDistance:Km'))
        .replace('ft', ' ' + this.translateService.instant('MapDistance:Feet'));
    });
  }

  private directionStyle(): Array<any> {
    const html = document.getElementsByTagName('html')[0];
    const defaultColor = getComputedStyle(html).getPropertyValue('--ten-map-default-pin-color');
    const activeColor = getComputedStyle(html).getPropertyValue('--ten-map-active-pin-color');

    // Example for a full style: https://github.com/mapbox/mapbox-gl-directions/blob/master/src/directions_style.js
    return [
      {
        id: 'directions-route-line-casing',
        type: 'line',
        source: 'directions',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#2d5f99',
          'line-width': 12,
        },
        filter: ['all', ['in', '$type', 'LineString'], ['in', 'route', 'selected']],
      },
      {
        id: 'directions-route-line',
        type: 'line',
        source: 'directions',
        layout: {
          'line-cap': 'butt',
          'line-join': 'round',
        },
        paint: {
          'line-color': {
            property: 'congestion',
            type: 'categorical',
            default: '#4882c5',
            stops: [
              ['unknown', '#4882c5'],
              ['low', '#4882c5'],
              ['moderate', '#f09a46'],
              ['heavy', '#e34341'],
              ['severe', '#8b2342'],
            ],
          },
          'line-width': 7,
        },
        filter: ['all', ['in', '$type', 'LineString'], ['in', 'route', 'selected']],
      },
      {
        id: 'directions-origin-point',
        type: 'circle',
        source: 'directions',
        paint: {
          'circle-radius': 18,
          'circle-color': activeColor,
        },
        filter: ['all', ['in', '$type', 'Point'], ['in', 'marker-symbol', 'A']],
      },
      {
        id: 'directions-destination-point',
        type: 'circle',
        source: 'directions',
        paint: {
          'circle-radius': 18,
          'circle-color': defaultColor,
        },
        filter: ['all', ['in', '$type', 'Point'], ['in', 'marker-symbol', 'B']],
      },
    ];
  }
}
